import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircleCheck, CircleDashed } from 'lucide-react';
import AuthLayout from './auth/AuthLayout';
import { Banner, Field } from '../design/ui';

const ShopifyRegister = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [ticketCount, setTicketCount] = useState(200);
  const [price, setPrice] = useState(490.00);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [token, setToken] = useState('');
  const [passwordError, setPasswordError] = useState('');
  
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    // Extract token from URL query parameters
    const searchParams = new URLSearchParams(location.search);
    const urlToken = searchParams.get('token');
    
    if (!urlToken) {
      setError(t('shopifyRegister.noTokenError'));
      return;
    }
    
    setToken(urlToken);
  }, [location, t]);

  // Fetch price when ticket count changes
  useEffect(() => {
    const fetchPrice = async () => {
      if (ticketCount < 200 || ticketCount > 3000) return;
      
      setLoadingPrice(true);
      try {
        const response = await axios.get(`/api/shopify/shopify/calculate-price`, {
          params: { ticket_count: ticketCount }
        });
        setPrice(response.data.price);
      } catch (err) {
        console.error('Error fetching price:', err);
      } finally {
        setLoadingPrice(false);
      }
    };

    const debounceTimer = setTimeout(fetchPrice, 300);
    return () => clearTimeout(debounceTimer);
  }, [ticketCount]);

  const validateForm = () => {
    if (!username || !password || !confirmPassword) {
      setError(t('shopifyRegister.allFieldsRequired'));
      return false;
    }

    if (password !== confirmPassword) {
      setPasswordError(t('shopifyRegister.passwordMismatch'));
      return false;
    }

    if (password.length < 8) {
      setPasswordError(t('shopifyRegister.passwordTooShort'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setError(null);
    setPasswordError('');
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Create form data
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);
      formData.append('token', token);
      formData.append('ticket_count', ticketCount);
      
      // Submit registration
      const response = await axios.post('/api/shopify/shopify/complete-registration', formData);
      
      if (response.data && response.data.confirmation_url) {
        // Redirect to Shopify payment confirmation page
        window.location.href = response.data.confirmation_url;
      } else {
        setError(t('shopifyRegister.unexpectedResponse'));
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.detail || t('shopifyRegister.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (error && !token) {
    return (
      <AuthLayout heading={t('shopifyRegister.errorTitle', 'Lien d’inscription invalide')}>
        <div className="u-form">
          <Banner tone="danger">{error}</Banner>
          <button
            type="button"
            className="u-btn u-btn--primary u-btn--block"
            onClick={() => (window.location.href = 'https://apps.shopify.com/unanimity-ai')}
          >
            {t('shopifyRegister.returnToShopify', 'Retourner sur Shopify')}
          </button>
        </div>
      </AuthLayout>
    );
  }

  const longEnough = password.length >= 8;

  return (
    <AuthLayout
      heading={t('shopifyRegister.completeRegistration', 'Terminer l’inscription')}
      subheading={t('shopifyRegister.shopifyConnection', 'Connexion depuis Shopify')}
      footer={(
        <>
          <span>{t('shopifyRegister.termsNotice')}</span>
          <a className="u-link" href="mailto:support@unanimity.ai">{t('shopifyRegister.needHelp', 'Besoin d’aide ?')}</a>
        </>
      )}
    >
      <form onSubmit={handleSubmit} className="u-form">
        {error && <Banner tone="danger">{error}</Banner>}
        <Field label={t('shopifyRegister.username', 'Identifiant')} htmlFor="username">
          <input
            type="text"
            id="username"
            className="u-input"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder={t('shopifyRegister.usernamePlaceholder')}
            disabled={loading}
            autoComplete="username"
          />
        </Field>
        <div className="u-form-grid">
          <Field label={t('shopifyRegister.password', 'Mot de passe')} htmlFor="password">
            <input
              type="password"
              id="password"
              className="u-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('shopifyRegister.passwordPlaceholder')}
              disabled={loading}
              autoComplete="new-password"
              aria-invalid={Boolean(passwordError)}
              aria-describedby={passwordError ? 'shopify-password-error' : 'shopify-password-rule'}
            />
          </Field>
          <Field label={t('shopifyRegister.confirmPassword', 'Confirmer')} htmlFor="confirmPassword">
            <input
              type="password"
              id="confirmPassword"
              className="u-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('shopifyRegister.confirmPasswordPlaceholder')}
              disabled={loading}
              autoComplete="new-password"
              aria-invalid={Boolean(passwordError)}
            />
          </Field>
        </div>
        {passwordError ? (
          <p className="u-error-text" id="shopify-password-error" role="alert">{passwordError}</p>
        ) : (
          <p className={`auth-rule ${longEnough ? 'is-met' : ''}`} id="shopify-password-rule">
            {longEnough ? <CircleCheck aria-hidden="true" /> : <CircleDashed aria-hidden="true" />}
            {t('shopifyRegister.minCharacters', '8 caractères minimum')}
          </p>
        )}

        <div className="auth-plan">
          <div className="auth-plan-head">
            <label className="u-label" htmlFor="ticketCount">{t('shopifyRegister.ticketCount', 'Tickets par mois')}</label>
            <input
              type="number"
              min="200"
              max="3000"
              value={ticketCount}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (value >= 200 && value <= 3000) {
                  setTicketCount(value);
                }
              }}
              disabled={loading}
              className="u-input auth-plan-number"
              aria-label={t('shopifyRegister.ticketCount', 'Tickets par mois')}
            />
          </div>
          <input
            type="range"
            id="ticketCount"
            min="200"
            max="3000"
            step="100"
            value={ticketCount}
            onChange={(e) => setTicketCount(parseInt(e.target.value))}
            disabled={loading}
            className="auth-range"
          />
          <div className="auth-plan-price">
            <span className="u-overline">{t('shopifyRegister.monthlyPrice', 'Prix mensuel')}</span>
            <span className="auth-plan-value u-num">
              {loadingPrice ? <span className="u-skeleton" style={{ width: 90, height: 22 }} /> : `$${price.toFixed(2)}`}
            </span>
            <span className="u-muted u-num">${(price / ticketCount).toFixed(2)} / {t('shopifyRegister.ticket', 'ticket')}</span>
          </div>
          <details className="auth-plan-tiers">
            <summary>{t('shopifyRegister.pricingTiers', 'Paliers de prix')}</summary>
            <ul>
              <li>200 {t('shopifyRegister.tickets', 'tickets')} : $490</li>
              <li>201–1000 : $0.90 / {t('shopifyRegister.ticket', 'ticket')}</li>
              <li>1001–2000 : $0.80 / {t('shopifyRegister.ticket', 'ticket')}</li>
              <li>2001–3000 : $0.70 / {t('shopifyRegister.ticket', 'ticket')}</li>
            </ul>
          </details>
        </div>

        <button type="submit" className="u-btn u-btn--primary u-btn--block" disabled={loading}>
          {loading ? t('shopifyRegister.registering', 'Inscription…') : t('shopifyRegister.completeRegistration', 'Terminer l’inscription')}
        </button>
      </form>
    </AuthLayout>
  );
};

export default ShopifyRegister;